import { ehLojaDigital } from '@/lib/loja-digital';
import { LinkDosJogadores } from '@/components/jogadores/LinkDosJogadores';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Copy,
  Check,
  Gamepad2,
  ShoppingCart,
  Users,
  Trophy,
  Wifi,
  ChevronLeft,
  ChevronRight,
  Newspaper,
  Sparkles,
  Calendar,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { useAuth } from '@/contexts/AuthContext';
import { canPublishNews } from '@/lib/services/news.service';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useTopBuyer } from '@/hooks/useTopBuyer';
import { usePosts } from '@/hooks/usePosts';
import { useGoal } from '@/hooks/useGoal';
import { useAvatar } from '@/hooks/useAvatar';
import { useMinecraftServerStatus } from '@/hooks/useMinecraftServerStatus';
import BlogCard from '@/components/blog/BlogCard';
import DiscordWidget from '@/components/widgets/DiscordWidget';
import TopBuyersWidget from '@/components/widgets/TopBuyersWidget';
import TopMonthWidget from '@/components/widgets/TopMonthWidget';
import GoalWidget from '@/components/widgets/GoalWidget';
import { ScrollReveal } from '@/components/common/ScrollReveal';
import { toast } from 'sonner';

export default function HomePage() {
  const { t } = useTranslation();
  const { config } = useTheme();
  const { store, loading: storeLoading } = useStore();
  const { isAuthenticated } = useAuth();
  const publicaNoticia = isAuthenticated && canPublishNews();
  const { copied, copy } = useCopyToClipboard();
  const { getAvatarUrl, getFullBodyUrl } = useAvatar();
  const { topBuyer, loading: loadingTopBuyer } = useTopBuyer(store?.id);
  const { goal, loading: loadingGoal } = useGoal(store?.id);
  
  const semServidor = ehLojaDigital(store);
  const serverIpToFetch = semServidor ? '' : store?.serverIp || config?.serverIp || 'play.hyterra.net';
  const { status: mcStatus, loading: mcLoading } = useMinecraftServerStatus(serverIpToFetch, store?.gameType);

  const [bgLoaded, setBgLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  // Preload hero background so it doesn't flash in after store data arrives
  useEffect(() => {
    const url = store?.heroBackgroundUrl;
    if (!url) { setBgLoaded(true); return; }
    setBgLoaded(false);
    const img = new Image();
    img.onload = () => setBgLoaded(true);
    img.onerror = () => setBgLoaded(true);
    img.src = url;
  }, [store?.heroBackgroundUrl]);
  
  // Fetch real posts from API
  const { posts: apiPosts, total: totalPosts, loading: loadingPosts } = usePosts(store?.id, currentPage, postsPerPage);

  const handleCopyIP = async () => {
    const success = await copy(store?.serverIp || config?.serverIp || 'play.hyterra.net');
    if (success) {
      toast.success(t('store.home.ip_copied'));
    }
  };

  // Pagination
  const totalPages = Math.ceil(totalPosts / postsPerPage);

  const formatMcVersion = (v: string) =>
    v.replace(/[^0-9./\-\s]/g, '').replace(/\//g, '-').replace(/\s+/g, ' ').trim();

  const heroLoading = storeLoading || !bgLoaded;

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen -mt-24 pt-24 flex items-center justify-center overflow-hidden">
        {/* Background Image — só aplica após preload para evitar flash */}
        <div
          className="absolute inset-0 -top-24 bg-cover bg-center hero-dynamic-bg transition-opacity duration-700"
          style={{
            backgroundImage: bgLoaded && store?.heroBackgroundUrl ? `url(${store.heroBackgroundUrl})` : undefined,
            opacity: bgLoaded ? 1 : 0,
          }}
        />

        {/* Overlay */}
        <div className="absolute inset-0 -top-24 bg-gradient-to-b from-background/20 via-background/50 to-background" />

        {/* Spinner enquanto store ou imagem carregam */}
        {heroLoading && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4">
            <div className="relative">
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
              <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl animate-pulse" />
            </div>
            <span className="text-sm text-muted-foreground animate-pulse">{t('store.home.loading')}</span>
          </div>
        )}

        {/* Content */}
        <div className={`relative z-10 container mx-auto px-4 md:px-6 lg:px-8 text-center transition-opacity duration-500 ${heroLoading ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          {/* Logo */}
          <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'backwards' }}>
            {store?.logoUrl ? (
              <img 
                src={store.logoUrl} 
                alt={store.name} 
                className="inline-block h-24 w-auto max-w-[300px] object-contain animate-float drop-shadow-2xl"
              />
            ) : (
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl bg-gradient-primary shadow-glow animate-float">
                <span className="text-4xl font-display font-bold text-primary-foreground">
                  {store?.name?.charAt(0) || 'H'}
                </span>
              </div>
            )}
          </div>
          
          {/* Title */}
          <h1 data-testid="hero-title" className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 max-w-3xl mx-auto leading-tight animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'backwards' }}>
            {store?.heroTitle || config?.heroTitle || t('store.home.hero.default_title')}
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.25s', animationFillMode: 'backwards' }}>
            {store?.heroSubtitle || config?.heroSubtitle || t('store.home.hero.default_subtitle')}
          </p>
          
          {/* Server Status (loja de produtos digitais não tem servidor) */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-8 animate-fade-in-up" style={{ animationDelay: '0.3s', animationFillMode: 'backwards' }}>
            {!semServidor && (store?.showServerStatus ?? true) && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-card/80 backdrop-blur-sm border border-border/50 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:border-primary/30">
                <div className={`w-2 h-2 rounded-full ${
                  store?.gameType === 'MINECRAFT'
                    ? (mcStatus?.online ? 'bg-success animate-pulse shadow-[0_0_10px_hsl(var(--success)/0.5)]' : 'bg-destructive')
                    : (config?.serverStatus === 'online' ? 'bg-success animate-pulse shadow-[0_0_10px_hsl(var(--success)/0.5)]' :
                       config?.serverStatus === 'maintenance' ? 'bg-warning animate-pulse shadow-[0_0_10px_hsl(var(--warning)/0.5)]' :
                       'bg-destructive')
                }`} />
                <Wifi className="w-4 h-4 text-muted-foreground transition-colors duration-300 group-hover:text-primary" />
                <span className="text-sm font-medium text-foreground">
                  {store?.gameType === 'MINECRAFT'
                    ? (mcLoading ? t('store.home.loading') : (mcStatus?.online ? t('store.home.online') : t('store.home.offline')))
                    : (config?.serverStatus === 'online' ? t('store.home.online') :
                       config?.serverStatus === 'maintenance' ? t('store.home.maintenance') :
                       t('store.home.offline'))}
                </span>
              </div>
            )}
            
            {/* A contagem segue "Mostrar jogadores online" do painel, como nos outros layouts. */}
            {!semServidor && (store?.showPlayerCount ?? true) && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-card/80 backdrop-blur-sm border border-border/50 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:border-primary/30">
                <Users className="w-4 h-4 text-primary animate-pulse" />
                <LinkDosJogadores>
                <span className="text-sm font-medium text-foreground">
                  <span className="tabular-nums">
                    {store?.gameType === 'MINECRAFT'
                      ? (mcStatus?.players?.online || 0)
                      : (config?.playersOnline || 0)}
                  </span> {t('store.home.players')}
                </span>
                </LinkDosJogadores>
              </div>
            )}

            {store?.gameType === 'MINECRAFT' && mcStatus?.online && mcStatus.version && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-card/80 backdrop-blur-sm border border-border/50 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:border-primary/30">
                <Gamepad2 className="w-4 h-4 text-icon" />
                <span className="text-sm font-medium text-foreground">
                  {t('store.home.version')} {formatMcVersion(mcStatus.version)}
                </span>
              </div>
            )}
          </div>
          
          {/* CTA Button */}
          {!semServidor && (store?.showServerIp ?? true) && (
            <Button
              size="lg"
              onClick={handleCopyIP}
              data-testid="copy-ip-button"
              className="bg-card/80 backdrop-blur-sm hover:bg-card-hover text-foreground border border-border/50 px-8 py-6 text-lg font-medium rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg hover:border-primary/30 animate-fade-in-up"
              style={{ animationDelay: '0.5s', animationFillMode: 'backwards' }}
            >
              {copied ? (
                <>
                  <Check className="w-5 h-5 mr-2 text-accent animate-pulse" />
                  {t('store.home.ip_copied')}
                </>
              ) : (
                <>
                  <Gamepad2 className="w-5 h-5 mr-2 animate-pulse" />
                  <span className="tabular-nums">{store?.serverIp || config?.serverIp || 'play.hyterra.net'}</span>
                </>
              )}
            </Button>
          )}
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-muted-foreground flex items-start justify-center p-1">
            <div className="w-1.5 h-3 rounded-full bg-muted-foreground animate-pulse" />
          </div>
        </div>
      </section>

      {/* Top Supporter Highlight Section */}
      {(store?.showTopBuyer ?? true) && (
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <ScrollReveal direction="scale">
            <div className="floating-card bg-card/50 backdrop-blur-sm border border-border/50 rounded-2xl overflow-hidden">
              <div className="p-6 md:p-8 lg:p-12">
                {/* Skin ao lado das informações já no tablet, e menor fora do desktop. */}
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                  {/* Top Supporter Card */}
                  <div className="order-2 md:order-1">
                  <Badge className="bg-primary/20 text-primary border-primary/30 mb-6 animate-pulse" data-testid="top-buyer-badge">
                    <Trophy className="w-3 h-3 mr-1" />
                    {t('store.home.top_buyer.badge')}
                  </Badge>

                  {topBuyer ? (
                    <>
                      <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
                        <span className="text-foreground">{t('store.home.top_buyer.title_with')}</span>
                        <br />
                        <span className="text-gradient-primary">{t('store.home.top_buyer.title_with_gradient')}</span>
                      </h2>

                      <p className="text-muted-foreground mb-8 text-lg">
                        {t('store.home.top_buyer.description')}
                      </p>

                      {/* Top Buyer Info */}
                      <div className="bg-background/50 backdrop-blur-sm rounded-xl p-6 border border-border/30 mb-6" data-testid="top-buyer-card">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="relative">
                            <img
                              src={getAvatarUrl(topBuyer.nickname, 128)}
                              alt={topBuyer.nickname}
                              className="w-16 h-16 rounded-full border-2 border-primary shadow-lg"
                            />
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center border-2 border-background">
                              <Trophy className="w-3 h-3 text-accent-foreground" />
                            </div>
                          </div>
                          <div>
                            <h3 className="font-display text-2xl font-bold text-foreground" data-testid="top-buyer-nickname">
                              {topBuyer.nickname}
                            </h3>
                            <p className="text-primary text-sm font-medium">{t('store.home.top_buyer.rank_label')}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-6">
                          <div className="bg-background/70 rounded-lg p-4 border border-border/20">
                            <p className="text-sm text-muted-foreground mb-1">{t('store.home.top_buyer.total_invested')}</p>
                            <p className="text-2xl font-display font-bold text-primary" data-testid="top-buyer-total">
                              R$ {(topBuyer.totalSpent / 100).toFixed(2)}
                            </p>
                          </div>
                          <div className="bg-background/70 rounded-lg p-4 border border-border/20">
                            <p className="text-sm text-muted-foreground mb-1">{t('store.home.top_buyer.purchases')}</p>
                            <p className="text-2xl font-display font-bold text-primary" data-testid="top-buyer-purchases">
                              {topBuyer.totalPurchases}
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
                        <span className="text-foreground">{t('store.home.top_buyer.title_empty')}</span>
                        <br />
                        <span className="text-gradient-primary">{t('store.home.top_buyer.title_empty_gradient')}</span>
                      </h2>

                      <p className="text-muted-foreground mb-8 text-lg">
                        {t('store.home.top_buyer.description_empty')}
                      </p>

                      {/* Empty State Card */}
                      <div className="bg-background/50 backdrop-blur-sm rounded-xl p-8 border border-dashed border-primary/30 mb-6 text-center" data-testid="top-buyer-empty">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                          <Trophy className="w-8 h-8 text-primary" />
                        </div>
                        <h3 className="font-display text-xl font-bold text-foreground mb-2">
                          {t('store.home.top_buyer.empty_state_title')}
                        </h3>
                        <p className="text-muted-foreground text-sm">
                          {t('store.home.top_buyer.empty_state_description')}
                        </p>
                      </div>
                    </>
                  )}

                  {/* CTA */}
                  <Link to="/loja">
                    <Button className="w-full bg-primary hover:bg-primary-hover text-primary-foreground transition-all hover:scale-105" data-testid="top-buyer-cta">
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      {topBuyer ? t('store.home.top_buyer.cta_support') : t('store.home.top_buyer.cta_first')}
                    </Button>
                  </Link>
                </div>
                
                {/* Character Display */}
                <div className="order-1 md:order-2 relative flex justify-center">
                  {topBuyer ? (
                    <div className="relative">
                      <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full"></div>
                      <img
                        src={getFullBodyUrl(topBuyer.nickname, 512)}
                        alt={`${topBuyer.nickname} Character`}
                        className="relative w-40 h-48 md:w-48 md:h-60 lg:w-80 lg:h-96 object-contain"
                        style={{
                          imageRendering: 'pixelated',
                          filter: 'drop-shadow(0 25px 50px hsl(var(--background)/0.8))'
                        }}
                      />
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="absolute inset-0 bg-primary/10 blur-3xl rounded-full"></div>
                      <div className="relative w-40 h-48 md:w-48 md:h-60 lg:w-80 lg:h-96 flex items-center justify-center">
                        <div className="text-center">
                          <div className="inline-flex items-center justify-center w-20 h-20 lg:w-32 lg:h-32 rounded-full bg-primary/10 mb-4 lg:mb-6 animate-pulse">
                            <Sparkles className="w-10 h-10 lg:w-16 lg:h-16 text-primary/50" />
                          </div>
                          <p className="text-muted-foreground text-lg font-medium">
                            {t('store.home.top_buyer.your_photo')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </ScrollReveal>
        </div>
      </section>
      )}

      {/* Notícias Section with Widgets */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          {/* Section Header */}
          <ScrollReveal>
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mb-4">
                <Newspaper className="w-6 h-6 text-primary" />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2" data-testid="news-section-title">
                {t('store.home.news.section_title')}
              </h2>
              {publicaNoticia && (
                <Button asChild variant="outline" size="sm" className="mt-3" data-testid="news-publish-button">
                  <Link to="/noticias/publicar">{t('store.home.news.publish')}</Link>
                </Button>
              )}
            </div>
          </ScrollReveal>
          
          {/* Layout: Notícias + Widgets */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Notícias Posts - 2/3 width on large screens */}
            <div className="lg:col-span-2 space-y-6">
              {/* Loading State */}
              {loadingPosts && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-card border border-border rounded-lg p-6 animate-pulse">
                      <div className="h-48 bg-muted rounded-lg mb-4"></div>
                      <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                      <div className="h-4 bg-muted rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Notícias Grid */}
              {!loadingPosts && apiPosts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {apiPosts.map((post, index) => {
                    // Adapt API post to BlogCard format
                    const adaptedPost = {
                      id: post.id,
                      title: post.title,
                      excerpt: post.content.replace(/<[^>]*>/g, '').substring(0, 150) + '...',
                      // O slug vem da API; o id é só a queda para notícia
                      // antiga, criada antes de o slug existir. Fixar `post.id`
                      // aqui anulava o slug mesmo com a API devolvendo certo.
                      slug: post.slug ?? post.id,
                      category: t('store.home.news.section_title'),
                      image: post.bannerUrl || 'https://cdn.hystore.com.br/hystore/11.jpg',
                      date: post.createdAt,
                      views: 0,
                      content: post.content,
                      author: {
                        name: post.authorNick,
                        role: 'Equipe',
                        avatar: getAvatarUrl(post.authorNick, 128),
                      },
                    };
                    
                    return (
                      <ScrollReveal key={post.id} direction="up" delay={index * 100}>
                        <BlogCard post={adaptedPost} />
                      </ScrollReveal>
                    );
                  })}
                </div>
              )}
              
              {/* Empty State */}
              {!loadingPosts && apiPosts.length === 0 && (
                <div className="text-center py-12 bg-card border border-border rounded-lg" data-testid="news-empty-state">
                  <Newspaper className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                    {t('store.home.news.empty_title')}
                  </h3>
                  <p className="text-muted-foreground">
                    {t('store.home.news.empty_description')}
                  </p>
                </div>
              )}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? 'default' : 'outline'}
                      size="icon"
                      onClick={() => setCurrentPage(page)}
                      className={currentPage === page ? 'bg-primary text-primary-foreground' : ''}
                    >
                      {page}
                    </Button>
                  ))}
                  
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Widgets Sidebar - 1/3 width on large screens */}
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="space-y-6 p-1">
                {/* Discord Widget */}
                {(store?.showDiscordWidget && store?.discordServerId) && (
                  <ScrollReveal direction="right" delay={100}>
                    <div className="floating-card">
                      <DiscordWidget serverId={store.discordServerId} />
                    </div>
                  </ScrollReveal>
                )}
                
                {/* Goal Widget - only show if active */}
                {!loadingGoal && goal && goal.isActive && (
                  <ScrollReveal direction="right" delay={200}>
                    <div className="floating-card">
                      <GoalWidget goal={goal} />
                    </div>
                  </ScrollReveal>
                )}
                
                {/* Top Buyers All Time */}
                {store?.showTopBuyersWidget && (
                  <ScrollReveal direction="right" delay={300}>
                    <div className="floating-card">
                      <TopBuyersWidget storeId={store.id} />
                    </div>
                  </ScrollReveal>
                )}
                
                {/* Top Buyers Monthly */}
                {store?.showTopMonthWidget && (
                  <ScrollReveal direction="right" delay={400}>
                    <div className="floating-card">
                      <TopMonthWidget storeId={store.id} />
                    </div>
                  </ScrollReveal>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
